<template>
    <div ref="rootElement" class="lean-rich-text-input" :class="{ 'lean-rich-text-input--rich': isRichText }">
        <span
            v-if="props.autoFocus"
            ref="focusSentinel"
            class="lean-rich-text-input__focus-sentinel"
            tabindex="0"
            @focus="focusActualInput"
        />
        <transition name="lean-rich-text-format" mode="out-in">
            <rich-text-editor
                v-if="isRichText"
                ref="richTextEditor"
                key="rich-text"
                v-model="richHtml"
                :editorStyle="autoGrowEditorStyle"
                @load="handleEditorLoad"
            />
            <textarea
                v-else
                key="plain-text"
                ref="plainTextArea"
                v-model="plainText"
                class="lean-rich-text-input__textarea"
                rows="1"
                :aria-label="inputLabel"
                :placeholder="placeholder"
                :disabled="props.disabled"
                @keydown.enter.exact.prevent="submitInput"
            />
        </transition>
        <div v-if="props.allowRichText || showCancel || canSubmit" class="lean-rich-text-input__toolbar">
            <button
                v-if="props.allowRichText"
                class="lean-rich-text-input__tool lean-rich-text-input__format"
                :class="{ 'lean-rich-text-input__tool--active': isRichText }"
                type="button"
                :aria-label="richTextButtonLabel"
                v-tooltip="richTextButtonLabel"
                @mousedown.prevent
                @click="handleRichTextToggle"
            >
                <span>A</span>
                <font-awesome-icon :icon="['fas', 'pen']" />
            </button>
            <button
                v-if="showCancel"
                class="lean-rich-text-input__tool"
                type="button"
                :aria-label="cancelLabel"
                v-tooltip="cancelLabel"
                @click="emit('cancel')"
            >
                <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
            <transition name="lean-rich-text-send">
                <button
                    v-if="canSubmit"
                    class="lean-rich-text-input__send"
                    type="button"
                    :aria-label="submitLabel"
                    v-tooltip="submitLabel"
                    :disabled="isSaving"
                    @click="submitInput"
                >
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </button>
            </transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    computed, nextTick, onMounted, ref, watch,
} from 'vue';
import { escapeHtml, isRichTextHtmlEmpty } from '@/utils/htmlUtils.ts';
import RichTextEditor from '@/components/common/RichTextEditor.vue';
import { IRichTextPendingImage } from '@/interfaces/deltaGpt/IRichTextImage.ts';

const props = withDefaults(defineProps<{
    placeholder: string;
    inputLabel: string;
    submitLabel: string;
    isSaving?: boolean;
    showCancel?: boolean;
    cancelLabel?: string;
    editorStyle?: string;
    initialHtml?: string;
    initiallyRichText?: boolean;
    forceRichText?: boolean;
    autoFocus?: boolean;
    allowRichText?: boolean;
    richTextToggleMode?: 'inline' | 'external';
    disabled?: boolean;
}>(), {
    isSaving: false,
    showCancel: false,
    cancelLabel: 'Cancel',
    editorStyle: 'height: 80px',
    initialHtml: '',
    initiallyRichText: false,
    forceRichText: false,
    autoFocus: false,
    allowRichText: true,
    richTextToggleMode: 'inline',
    disabled: false,
});

const emit = defineEmits<{
    submit: [string];
    cancel: [];
    'request-rich-text': [string];
}>();

const shouldStartRichText = props.allowRichText
    && props.richTextToggleMode === 'inline'
    && (props.initiallyRichText || props.forceRichText);
const plainText = ref<string>(shouldStartRichText ? '' : richTextToPlainText(props.initialHtml));
const richHtml = ref<string>(shouldStartRichText ? props.initialHtml : '');
const isRichText = ref<boolean>(shouldStartRichText);
const rootElement = ref<HTMLElement | null>(null);
const plainTextArea = ref<HTMLTextAreaElement | null>(null);
const focusSentinel = ref<HTMLElement | null>(null);
const richTextEditor = ref<InstanceType<typeof RichTextEditor> | null>(null);

const currentHtml = computed<string>(() => {
    if (isRichText.value) return richHtml.value;
    return plainTextToHtml(plainText.value);
});

const canSubmit = computed<boolean>(() => !props.disabled && !props.isSaving && !isRichTextHtmlEmpty(currentHtml.value));
const richTextButtonLabel = computed<string>(() => (
    props.richTextToggleMode === 'external'
        ? 'Open rich text editor'
        : 'Toggle formatting'
));
const autoGrowEditorStyle = computed<string>(() => convertHeightToMinHeight(props.editorStyle));

onMounted(() => {
    if (!props.autoFocus) return;

    focusSentinel.value?.setAttribute('autofocus', '');
});

function plainTextToHtml (text: string): string {
    return text
        .trim()
        .split(/\n{2,}/)
        .map(paragraph => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
        .join('');
}

function richTextToPlainText (html: string): string {
    const template = document.createElement('template');
    template.innerHTML = html;

    template.content.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
    template.content.querySelectorAll('p').forEach(paragraph => paragraph.append('\n'));

    return template.content.textContent
        ?.replace(/\u00A0/g, ' ')
        .trim() ?? '';
}

function convertHeightToMinHeight (style: string): string {
    return style.replace(/(^|;)\s*height\s*:/gi, '$1min-height:');
}

function handleRichTextToggle (): void {
    if (props.disabled || !props.allowRichText) return;
    if (props.richTextToggleMode === 'external') {
        emit('request-rich-text', currentHtml.value);
        return;
    }

    toggleRichText();
}

function toggleRichText (): void {
    if (props.disabled || !props.allowRichText) return;

    isRichText.value = !isRichText.value;
    if (isRichText.value) {
        if (!richHtml.value && plainText.value.trim()) {
            richHtml.value = plainTextToHtml(plainText.value);
        }
        return;
    }

    if (!plainText.value && !isRichTextHtmlEmpty(richHtml.value)) {
        plainText.value = richTextToPlainText(richHtml.value);
    }
    resizePlainTextArea();
}

function submitInput (): void {
    if (!canSubmit.value) return;
    emit('submit', currentHtml.value);
}

function clear (): void {
    plainText.value = '';
    richHtml.value = '';
    isRichText.value = props.forceRichText;
    resizePlainTextArea();
}

function getPendingImages (): Array<IRichTextPendingImage> {
    return richTextEditor.value?.getPendingImages() ?? [];
}

async function openRichText (): Promise<void> {
    if (props.disabled || !props.allowRichText) return;

    if (!isRichText.value) {
        toggleRichText();
    }

    await focus(true);
}

async function focusActualInput (): Promise<void> {
    await focus(true);
}

async function handleEditorLoad (): Promise<void> {
    if (!props.autoFocus) return;

    await focus(true);
}

async function focus (preventScroll: boolean = false): Promise<void> {
    if (props.disabled) return;

    await nextTick();

    const focusTarget = getCurrentFocusTarget();

    if (focusTarget) {
        focusTarget.focus({ preventScroll });
    }
}

function getCurrentFocusTarget (): HTMLElement | null {
    const richTextEditorElement = rootElement.value?.querySelector<HTMLElement>('.ql-editor') ?? null;
    const plainTextEditor = plainTextArea.value
        ?? rootElement.value?.querySelector<HTMLTextAreaElement>('textarea')
        ?? null;

    return isRichText.value
        ? richTextEditorElement ?? plainTextEditor
        : plainTextEditor ?? richTextEditorElement;
}

function scrollIntoView (): void {
    const element = rootElement.value;
    if (!element) return;

    const scrollParent = getScrollParent(element);
    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollParent.getBoundingClientRect();
    const nextTop = scrollParent.scrollTop
        + elementRect.top
        - parentRect.top
        - ((scrollParent.clientHeight - elementRect.height) / 2);

    scrollParent.scrollTo({
        top: Math.max(0, nextTop),
        behavior: 'smooth',
    });
}

function getScrollParent (element: HTMLElement): HTMLElement {
    let parent = element.parentElement;

    while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        const canScroll = ['auto', 'scroll'].includes(overflowY) && parent.scrollHeight > parent.clientHeight;
        if (canScroll) return parent;

        parent = parent.parentElement;
    }

    return document.documentElement;
}

watch(plainText, resizePlainTextArea);
resizePlainTextArea();

async function resizePlainTextArea (): Promise<void> {
    await nextTick();
    if (!plainTextArea.value) return;

    plainTextArea.value.style.height = 'auto';
    plainTextArea.value.style.height = `${plainTextArea.value.scrollHeight}px`;
}

defineExpose({
    clear,
    focus,
    getPendingImages,
    openRichText,
    scrollIntoView,
});
</script>

<style scoped lang="scss">
.lean-rich-text-input {
    container: lean-rich-text-input / inline-size;
    position: relative;
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusOuter);
    background: var(--neutral0);
    overflow: hidden;

    &:focus-within {
        border-color: var(--actionColour);
    }

    :deep(.p-editor-container) {
        border: 0;
    }

    :deep(.ql-toolbar) {
        border-width: 0 0 1px;
        padding-right: 8rem;
    }

    :deep(.ql-container) {
        border: 0;
    }

    :deep(.ql-editor) {
        min-height: 5rem;
        padding-right: 8rem;
        padding-bottom: 2.75rem;
    }
}

.lean-rich-text-input__focus-sentinel {
    position: fixed;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.lean-rich-text-input__textarea {
    width: 100%;
    min-height: 3rem;
    padding: var(--smallSpacing) 8rem var(--smallSpacing) var(--defaultSpacing);
    border: 0;
    resize: none;
    background: transparent;
    color: var(--primaryFont);
    font: inherit;
    line-height: 1.45;
    overflow: hidden;

    &:focus {
        outline: none;
    }
}

.lean-rich-text-input__toolbar {
    position: absolute;
    right: var(--smallSpacing);
    bottom: var(--smallSpacing);
    display: flex;
    width: 7.75rem;
    align-items: center;
    justify-content: flex-end;
    gap: var(--extraSmallSpacing);
    transition: gap 0.2s ease;
}

.lean-rich-text-input__tool,
.lean-rich-text-input__send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.35rem;
    height: 2.35rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--actionColour);
    cursor: pointer;
    transition: color 0.18s ease, transform 0.18s ease, opacity 0.18s ease;

    span {
        font-weight: var(--fontWeightBold);
        font-size: var(--defaultFontSize);
        line-height: 1;
    }

    &:hover,
    &:focus-visible {
        color: var(--actionColour);
        outline: none;
    }

    &:disabled {
        cursor: wait;
        opacity: 0.5;
    }
}

.lean-rich-text-input__format {
    position: relative;

    span {
        position: absolute;
        top: 0.42rem;
        left: 0.38rem;
    }

    svg {
        position: absolute;
        right: 0.32rem;
        bottom: 0.32rem;
        width: 0.95rem;
        height: 0.95rem;
    }
}

.lean-rich-text-input__tool--active,
.lean-rich-text-input__send {
    color: var(--actionColour);
}

.lean-rich-text-input__send:hover,
.lean-rich-text-input__send:focus-visible {
    background: var(--neutral400);
}

.lean-rich-text-format-enter-active,
.lean-rich-text-format-leave-active {
    overflow: hidden;
    transition: max-height 0.22s ease, opacity 0.16s ease, transform 0.22s ease;
}

.lean-rich-text-format-enter-from,
.lean-rich-text-format-leave-to {
    max-height: 3rem;
    opacity: 0;
    transform: translateY(calc(-1 * var(--extraSmallSpacing)));
}

.lean-rich-text-format-enter-to,
.lean-rich-text-format-leave-from {
    max-height: 12rem;
    opacity: 1;
    transform: translateY(0);
}

.lean-rich-text-send-enter-active,
.lean-rich-text-send-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease, width 0.18s ease, margin 0.18s ease;
}

.lean-rich-text-send-enter-from,
.lean-rich-text-send-leave-to {
    width: 0;
    margin-left: calc(-1 * var(--extraSmallSpacing));
    opacity: 0;
    transform: translateX(0.8rem) scale(0.85);
}

@container lean-rich-text-input (max-width: 34rem) {
    .lean-rich-text-input__textarea {
        padding-right: var(--defaultSpacing);
        padding-bottom: var(--smallSpacing);
    }

    .lean-rich-text-input__toolbar {
        position: static;
        justify-content: flex-end;
        width: 100%;
        padding: 0 var(--smallSpacing) var(--smallSpacing);
        margin-left: auto;
        background: var(--neutral0);
    }

    :deep(.ql-toolbar),
    :deep(.ql-editor) {
        padding-right: var(--defaultSpacing);
    }

    :deep(.ql-editor) {
        padding-bottom: var(--smallSpacing);
    }
}
</style>
